import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { GiftIcon, XIcon } from 'lucide-react';
import { useExitIntent } from '../hooks/useExitIntent';
const SPECIAL_OFFER_URL = 'https://megapack-colorimetria.impultienda.ar/oferta-especial?store_id=54657d15-3c8d-4895-b70a-6d88eadea1f5';
export function ExitIntentModal() {
  const {
    closeOffer,
    isOpen
  } = useExitIntent();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeOffer();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusableElements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeOffer, isOpen]);
  return <AnimatePresence data-id="element-167" data-mp="1a234hzcrcwmp">
      {isOpen && <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} role="presentation" data-id="element-168" data-mp="1a35s9yesaiye">
          <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="exit-offer-title" aria-describedby="exit-offer-description" className="relative w-full max-w-md overflow-hidden rounded-[20px] border-2 border-brandGold bg-bg-card p-6 text-center shadow-[0_0_60px_rgba(212,162,76,0.3)] md:p-8" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 26
      }} data-id="element-169" data-mp="2gif321jhsgit">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-brandGold/10" data-id="element-170" data-mp="zgnwsnzxf3f1" />
            <button ref={closeButtonRef} type="button" onClick={closeOffer} className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full border border-line bg-bg-highlight text-txt-muted transition-colors hover:border-brandGold hover:text-txt-primary focus:outline-none focus:ring-2 focus:ring-brandGold" aria-label="Cerrar oferta exclusiva" data-id="element-171" data-mp="14e5ufi8htw7q">
              <XIcon size={18} aria-hidden="true" data-id="element-172" data-mp="470rgt5pslq4" />
            </button>

            <div className="relative mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-brandGold/40 bg-brandGold/15 text-brandGold shadow-[0_0_24px_rgba(212,162,76,0.25)]" data-id="element-173" data-mp="44oekuetd1jx">
              <GiftIcon size={28} aria-hidden="true" data-id="element-174" data-mp="3rdt9y06dlnhs" />
            </div>
            <p className="relative mb-2 font-sans text-[10px] font-bold uppercase tracking-[1.8px] text-brandGold" data-id="element-175" data-mp="3mdou8cdakwsm">
              Oferta exclusiva por ahora
            </p>
            <h2 id="exit-offer-title" className="relative mb-4 font-display text-[22px] font-bold uppercase leading-[1.25] tracking-[0.5px] text-txt-primary md:text-[30px]" data-id="element-176" data-mp="3cnm2efhwpovv">
              ¡Espera! Tenemos un regalo para ti{' '}
              <span aria-hidden="true" data-id="element-177" data-mp="2yn1ljovjy350">🎁</span>
            </h2>
            <p id="exit-offer-description" className="relative mx-auto mb-6 max-w-sm font-sans text-[14px] leading-[1.65] text-txt-body md:text-[16px]" data-id="element-178" data-mp="2yow6v4aqjx9m">
              Antes de irte, mira esta oferta exclusiva con un descuento que
              solo está disponible ahora. Pagas en tu moneda local.
            </p>

            <div className="relative flex flex-col gap-3" data-id="element-179" data-mp="xs50tqxwj315">
              <a href={SPECIAL_OFFER_URL} rel="noopener" className="inline-flex w-full items-center justify-center rounded-[14px] bg-brandGold px-6 py-4 font-sans text-[15px] font-extrabold uppercase tracking-wide text-[#14100A] shadow-[0_0_28px_rgba(212,162,76,0.4)] transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-brandGold-hover hover:shadow-[0_0_42px_rgba(212,162,76,0.55)] focus:outline-none focus:ring-2 focus:ring-brandGold focus:ring-offset-2 focus:ring-offset-bg-card" data-id="element-180" data-mp="xqlna83mr632">
                Ver oferta
              </a>
              <button type="button" onClick={closeOffer} className="inline-flex w-full items-center justify-center rounded-[14px] border border-line bg-bg-highlight px-6 py-3.5 font-sans text-[14px] font-bold text-txt-muted transition-colors hover:border-brandGold/50 hover:text-txt-primary focus:outline-none focus:ring-2 focus:ring-brandGold focus:ring-offset-2 focus:ring-offset-bg-card" data-id="element-181" data-mp="2jb8sz8bj6yok">
                No, gracias
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}